import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export type LayoutType = 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST';
export type ButtonStyle = 'SOLID' | 'OUTLINE' | 'GHOST' | 'GRADIENT' | 'GLASS';
export type SidebarStyle = 'DARK' | 'LIGHT' | 'TRANSPARENT';
export type ThemeMode = 'LIGHT' | 'DARK';
export type ThemeStatus = 'DRAFT' | 'PUBLISHED';

export interface ColorScale {
  '50': string;
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '950': string;
}

export interface TenantTheme {
  id: string;
  tenantId: string;
  brandName: string;
  displayName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColors: ColorScale;
  accentColors: ColorScale;
  surfaceColors: ColorScale;
  fontFamily?: string;
  fontFamilyMono?: string;
  borderRadius?: string;
  buttonStyle: ButtonStyle;
  sidebarStyle: SidebarStyle;
  loginBgImage?: string;
  loginBgGradient?: string;
  darkModeEnabled: boolean;
  defaultMode: ThemeMode;
  status: ThemeStatus;
  layoutType: LayoutType;
  buttonVariant?: string;
  cardVariant?: string;
  tableVariant?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateThemeDto {
  brandName?: string;
  displayName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColors?: ColorScale;
  accentColors?: ColorScale;
  surfaceColors?: ColorScale;
  fontFamily?: string;
  fontFamilyMono?: string;
  borderRadius?: string;
  buttonStyle?: ButtonStyle;
  sidebarStyle?: SidebarStyle;
  loginBgImage?: string;
  loginBgGradient?: string;
  darkModeEnabled?: boolean;
  defaultMode?: ThemeMode;
  layoutType?: LayoutType;
  buttonVariant?: string;
  cardVariant?: string;
  tableVariant?: string;
}

export interface ThemeExport {
  version: string;
  exportedAt: string;
  theme: TenantTheme;
  variants?: UserVariantExport[];
}

export interface UserVariantExport {
  userType: string;
  layoutType?: LayoutType;
  accentColorOverride?: ColorScale;
  buttonVariant?: string;
  cardVariant?: string;
  tableVariant?: string;
  menuConfig?: Record<string, unknown>;
  widgetConfig?: Record<string, unknown>[];
}

// Hook for managing tenant themes
export function useTheme(tenantId: string) {
  const queryClient = useQueryClient();

  // Get theme for tenant
  const { data: theme, isLoading, error } = useQuery({
    queryKey: ['theme', tenantId],
    queryFn: async () => {
      const response = await api.get<TenantTheme>(`/themes/${tenantId}`);
      return response.data;
    },
    enabled: !!tenantId,
  });

  // Update theme
  const updateTheme = useMutation({
    mutationFn: async (data: UpdateThemeDto) => {
      const response = await api.put<TenantTheme>(`/themes/${tenantId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme', tenantId] });
    },
  });

  // Update layout type
  const updateLayout = useMutation({
    mutationFn: async (layoutType: LayoutType) => {
      const response = await api.put<TenantTheme>(`/themes/${tenantId}/layout`, null, {
        params: { layoutType },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme', tenantId] });
    },
  });

  // Publish theme
  const publishTheme = useMutation({
    mutationFn: async () => {
      const response = await api.post<TenantTheme>(`/themes/${tenantId}/publish`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme', tenantId] });
    },
  });

  // Revert theme
  const revertTheme = useMutation({
    mutationFn: async () => {
      const response = await api.post<TenantTheme>(`/themes/${tenantId}/revert`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme', tenantId] });
    },
  });

  // Export theme (basic)
  const exportTheme = useMutation({
    mutationFn: async () => {
      const response = await api.get<ThemeExport>(`/themes/${tenantId}/export`);
      return response.data;
    },
  });

  // Export theme enhanced (with variants and widgets)
  const exportThemeEnhanced = useMutation({
    mutationFn: async () => {
      const response = await api.get<ThemeExport>(`/themes/${tenantId}/export-enhanced`);
      return response.data;
    },
  });

  // Import theme (basic)
  const importTheme = useMutation({
    mutationFn: async (data: ThemeExport) => {
      const response = await api.post<TenantTheme>(`/themes/${tenantId}/import`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme', tenantId] });
    },
  });

  // Import theme enhanced (with variants and widgets)
  const importThemeEnhanced = useMutation({
    mutationFn: async (data: ThemeExport) => {
      const response = await api.post<TenantTheme>(`/themes/${tenantId}/import-enhanced`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['user-variants', tenantId] });
    },
  });

  // Copy theme to another tenant
  const copyTheme = useMutation({
    mutationFn: async (toTenantId: string) => {
      const response = await api.post<TenantTheme>(`/themes/${tenantId}/copy/${toTenantId}`);
      return response.data;
    },
    onSuccess: (_, toTenantId) => {
      queryClient.invalidateQueries({ queryKey: ['theme', toTenantId] });
    },
  });

  // Delete theme
  const deleteTheme = useMutation({
    mutationFn: async () => {
      await api.delete(`/themes/${tenantId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme', tenantId] });
    },
  });

  return {
    theme,
    isLoading,
    error,
    updateTheme,
    updateLayout,
    publishTheme,
    revertTheme,
    exportTheme,
    exportThemeEnhanced,
    importTheme,
    importThemeEnhanced,
    copyTheme,
    deleteTheme,
  };
}

// Hook for getting effective theme (with user variant applied)
export function useEffectiveTheme(tenantId: string, userType: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['effective-theme', tenantId, userType],
    queryFn: async () => {
      const response = await api.get(`/themes/${tenantId}/effective/${userType}`);
      return response.data;
    },
    enabled: !!tenantId && !!userType,
  });

  return {
    effectiveTheme: data,
    isLoading,
    error,
  };
}

export default useTheme;
